import { SortDirection } from "@tanstack/table-core/src/features/Sorting";
import { last, orderBy } from "lodash";
import { DropdownOption } from "PFComponents/dropdown/dropdown";
import { useThresholds } from "PFCore/hooks/queries/bookings/thresholds/use_thresholds";
import { Booking } from "PFTypes";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Overbooking } from "../../../../types";

type UseOverbookingRangesProps = {
  bookings?: (Partial<Booking> & { id: number })[];
  overbookings: Overbooking[];
};

export type OverbookingsRange = {
  start: string;
  end: string;
  overbookings: Overbooking[];
  bookings: (Partial<Booking> & { id: number })[];
};

export const useOverbookingRanges = ({ bookings, overbookings }: UseOverbookingRangesProps) => {
  const { t } = useTranslation("bookingModule", { keyPrefix: "details.overbookings" });
  const thresholds = useThresholds(["overbooking"]);

  const sortOptions: DropdownOption[] = [
    { id: "asc", displayElement: t("dateAsc"), item: "asc" },
    { id: "desc", displayElement: t("dateDesc"), item: "desc" }
  ];

  const [selectedSort, setSelectedSort] = useState<SortDirection>(sortOptions[0].item);

  const rangesWithBookings: OverbookingsRange[] = useMemo(() => {
    if (!bookings?.length) {
      return [];
    }
    const intervals: Omit<OverbookingsRange, "bookings">[] = overbookings.reduce(
      (acc: OverbookingsRange[], curr) => {
        const { available_minutes, date, utilization } = curr;
        const lastRange = last(acc);
        if (!lastRange) {
          return [{ start: date, end: date, overbookings: [curr] }];
        }
        const currentRangeAvailableMinutes = lastRange.overbookings[0].available_minutes;
        const currentRangeUtilization = lastRange.overbookings[0].utilization;
        const currentRangeThreshold = thresholds?.overbooking?.find(
          (item) => currentRangeUtilization * 100 >= item.min && currentRangeUtilization * 100 <= item.max
        );
        const currentItemThreshold = thresholds?.overbooking?.find(
          (item) => utilization * 100 >= item.min && utilization * 100 <= item.max
        );
        const isNextInRange =
          currentRangeAvailableMinutes === available_minutes &&
          currentRangeThreshold === currentItemThreshold;

        if (isNextInRange) {
          return [
            ...acc.slice(0, -1),
            {
              ...lastRange,
              end: date,
              overbookings: [...lastRange.overbookings, curr]
            }
          ];
        }
        return [...acc, { start: date, end: date, overbookings: [curr] }];
      },
      []
    );
    const overbookingRanges: OverbookingsRange[] = intervals.map(({ start, end, overbookings }) => {
      const filteredBookings =
        (bookings || []).filter(
          ({ start_date, end_date }) =>
            start_date &&
            end_date &&
            new Date(start).getTime() <= new Date(end_date).getTime() &&
            new Date(start_date).getTime() <= new Date(end).getTime()
        ) || [];
      return { start, end, bookings: filteredBookings, overbookings };
    });

    return overbookingRanges;
  }, [overbookings, bookings]);

  const sortedRanges = useMemo(
    () =>
      orderBy(
        rangesWithBookings,
        [({ start }) => new Date(start).getTime(), ({ end }) => new Date(end).getTime()],
        [selectedSort, selectedSort]
      ),
    [rangesWithBookings, selectedSort]
  );

  return { rangesWithBookings: sortedRanges, selectedSort, setSelectedSort, sortOptions };
};
